import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Receipt, Download, Send, Check, X, MoreHorizontal, Printer, Euro, Trash2, XCircle, Filter, Bell } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const invoiceStatusConfig = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  envoyee: { label: 'Envoyé', color: 'bg-blue-100 text-blue-800' },
  en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
  partiel: { label: 'Partiel', color: 'bg-orange-100 text-orange-800' },
  payee: { label: 'Réglé', color: 'bg-emerald-100 text-emerald-800' },
  trop_percu: { label: 'Trop-perçu', color: 'bg-orange-200 text-orange-900' },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-800' },
};

export function InvoicesTab({
  invoices, filteredInvoices, paginatedInvoices, loading,
  invoiceStatusFilter, setInvoiceStatusFilter,
  invoiceStartDate, setInvoiceStartDate,
  invoiceEndDate, setInvoiceEndDate,
  invoicePageSize, setInvoicePageSize,
  invoiceCurrentPage, setInvoiceCurrentPage, invoiceTotalPages,
  isUser, isAdmin,
  resetInvoiceFilters,
  onDownloadPdf, onPrint, onSend, onLoadReminders,
  onRecordPayment, onRecordRegularization, onStatusChange, onCancel, onDelete,
}) {
  return (
    <>
      <Card className="border-slate-200 mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">Filtres :</span>
            </div>
            <div className="flex-1 min-w-[150px] max-w-[180px]">
              <Label className="text-xs text-slate-500 mb-1 block">Statut</Label>
              <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                <SelectTrigger data-testid="invoice-status-filter" className="h-9">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="brouillon">Brouillon</SelectItem>
                  <SelectItem value="envoyee">Envoyé</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="partiel">Partiel</SelectItem>
                  <SelectItem value="payee">Réglé</SelectItem>
                  <SelectItem value="trop_percu">Trop-perçu</SelectItem>
                  <SelectItem value="annule">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Date début</Label>
              <Input type="date" value={invoiceStartDate} onChange={(e) => setInvoiceStartDate(e.target.value)} className="h-9" data-testid="invoice-start-date-filter" />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-slate-500 mb-1 block">Date fin</Label>
              <Input type="date" value={invoiceEndDate} onChange={(e) => setInvoiceEndDate(e.target.value)} className="h-9" data-testid="invoice-end-date-filter" />
            </div>
            {(invoiceStatusFilter !== 'all' || invoiceStartDate || invoiceEndDate) && (
              <Button variant="ghost" size="sm" onClick={resetInvoiceFilters} className="text-slate-500 hover:text-slate-700">
                <X className="h-4 w-4 mr-1" /> Effacer
              </Button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-slate-500">Lignes :</span>
              <Select value={String(invoicePageSize)} onValueChange={(v) => setInvoicePageSize(v === 'all' ? 'all' : Number(v))}>
                <SelectTrigger className="h-8 w-20" data-testid="invoice-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="60">60</SelectItem>
                  <SelectItem value="120">120</SelectItem>
                  <SelectItem value="all">Tous</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-500">{filteredInvoices.length} / {invoices.length} résultat(s)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" style={{ width: 40, height: 40 }} /></div>
      ) : filteredInvoices.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Receipt className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">{invoices.length === 0 ? "Aucun appel à participation" : "Aucun résultat pour ces filtres"}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° Document</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Bénéficiaire</TableHead>
                <TableHead>Participation</TableHead>
                <TableHead>Versé</TableHead>
                <TableHead>Échéance</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedInvoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={invoice.invoice_number?.startsWith('NDD') ? 'border-blue-500 text-blue-700' : 'border-orange-500 text-orange-700'}>
                      {invoice.invoice_number?.startsWith('NDD') ? 'Note de Débit' : 'Appel à Part.'}
                    </Badge>
                  </TableCell>
                  <TableCell>{invoice.client_name}</TableCell>
                  <TableCell>{invoice.amount.toLocaleString('fr-FR')} €</TableCell>
                  <TableCell>{(invoice.amount_paid || 0).toLocaleString('fr-FR')} €</TableCell>
                  <TableCell>{format(parseISO(invoice.due_date), 'd MMM yyyy', { locale: fr })}</TableCell>
                  <TableCell>
                    <Badge className={invoiceStatusConfig[invoice.status]?.color}>{invoiceStatusConfig[invoice.status]?.label}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onDownloadPdf(invoice)}><Download className="mr-2 h-4 w-4" /> Télécharger PDF</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onPrint(invoice)}><Printer className="mr-2 h-4 w-4" /> Imprimer</DropdownMenuItem>
                        {!isUser && (
                          <>
                            <DropdownMenuItem onClick={() => onSend(invoice.id)}><Send className="mr-2 h-4 w-4" /> Envoyer par email</DropdownMenuItem>
                            {invoice.status !== 'payee' && invoice.status !== 'annule' && (
                              <DropdownMenuItem onClick={() => onLoadReminders(invoice)}><Bell className="mr-2 h-4 w-4 text-amber-600" /> Gérer les relances</DropdownMenuItem>
                            )}
                            {invoice.status !== 'payee' && (
                              <DropdownMenuItem onClick={() => onRecordPayment(invoice)}><Euro className="mr-2 h-4 w-4" /> Enregistrer règlement</DropdownMenuItem>
                            )}
                            {(invoice.status === 'trop_percu' || invoice.status === 'payee') && (
                              <DropdownMenuItem
                                onClick={() => onRecordRegularization(invoice)}
                                data-testid={`regularize-invoice-${invoice.id}`}
                              >
                                <Euro className="mr-2 h-4 w-4 text-orange-600" /> Saisir une régularisation
                              </DropdownMenuItem>
                            )}
                            {invoice.status !== 'payee' && (
                              <DropdownMenuItem onClick={() => onStatusChange(invoice.id, 'payee')} data-testid={`mark-paid-invoice-${invoice.id}`}><Check className="mr-2 h-4 w-4 text-emerald-600" /> Marquer réglé</DropdownMenuItem>
                            )}
                            {invoice.status === 'payee' && (
                              <DropdownMenuItem onClick={() => onStatusChange(invoice.id, 'en_attente')} data-testid={`unmark-paid-invoice-${invoice.id}`}><X className="mr-2 h-4 w-4 text-amber-600" /> Annuler règlement</DropdownMenuItem>
                            )}
                            {isAdmin && (
                              <>
                                {invoice.status !== 'annule' && (
                                  <DropdownMenuItem onClick={() => onCancel(invoice)} className="text-amber-600"><XCircle className="mr-2 h-4 w-4" /> Annuler l'appel</DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => onDelete(invoice)} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Supprimer</DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {filteredInvoices.length > 0 && invoicePageSize !== 'all' && (
        <div className="flex items-center justify-between mt-4 px-2">
          <p className="text-sm text-slate-500">Page {invoiceCurrentPage} sur {invoiceTotalPages} ({paginatedInvoices.length} affichés sur {filteredInvoices.length})</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setInvoiceCurrentPage(1)} disabled={invoiceCurrentPage === 1}>««</Button>
            <Button variant="outline" size="sm" onClick={() => setInvoiceCurrentPage(p => Math.max(1, p - 1))} disabled={invoiceCurrentPage === 1}>Précédent</Button>
            <Button variant="outline" size="sm" onClick={() => setInvoiceCurrentPage(p => Math.min(invoiceTotalPages, p + 1))} disabled={invoiceCurrentPage === invoiceTotalPages}>Suivant</Button>
            <Button variant="outline" size="sm" onClick={() => setInvoiceCurrentPage(invoiceTotalPages)} disabled={invoiceCurrentPage === invoiceTotalPages}>»»</Button>
          </div>
        </div>
      )}
    </>
  );
}
